import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Icon,
  VStack,
  Stack,
  Button,
  Spacer,
  Flex,
  Text,
  IconButton,
  Heading,
  Separator,
  Box,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { PlusIcon, XMarkIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import { SortableList } from '@/components/sortable-list';
import CreateContentTypeField from './create-content-type-field';
import CreateSectionTitleModal from '@/pages/fields/create-section-title-modal';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import api from '@/utils/api';
import { FIELD_TYPES } from '@/lib/constants';
import { StaticField, BlockType } from '@/lib/types';

interface Props {
  workspaceId: string;
  contentTypeId: string;
}

interface ContentTypeField {
  id: number;
  field_id: number;
  workspace_id: number;
  content_type_id: number;
  field_name: string;
  field_type: number;
  field_unique_name: string;
  properties: any;
  sort_order: number;
}

const isStaticField = (fieldUniqueName: string) => {
  return [StaticField.Name, StaticField.CreatedDate, StaticField.UpdatedDate].includes(
    fieldUniqueName as StaticField,
  );
};

const getFieldName = (fieldType: number) => {
  return FIELD_TYPES.find((field) => field.id === fieldType)?.name || 'Unknown';
};

const ContentTypeItem: React.FC<{ item: ContentTypeField; reload: () => void }> = ({
  item,
  reload,
}) => {
  async function handleRemove(item: ContentTypeField) {
    try {
      await api.delete(`content-type-fields/${item.id}`);

      toaster.create({
        title: 'Field was successfully removed',
        type: 'success',
      });

      reload();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete field',
      });
    }
  }

  return (
    <Stack
      key={item.id}
      borderWidth={'1px'}
      borderRadius={'lg'}
      p={3}
      ms={5}
      bg={isStaticField(item.field_unique_name) ? 'gray.100' : ''}
    >
      <Flex justifyContent={'center'}>
        <Stack direction={'column'} gap={1}>
          <Text>{item.field_name}</Text>
          <Text fontSize={'xs'}>
            <Text as={'strong'} display={'inline-block'}>
              {getFieldName(item.field_type)}
            </Text>{' '}
            field
          </Text>
        </Stack>
        <Spacer />
        {!isStaticField(item.field_unique_name) ? (
          <>
            <Tooltip content={'Delete'} aria-label={'Delete'}>
              <IconButton
                aria-label={'Delete'}
                variant={'ghost'}
                size={'sm'}
                onClick={() => handleRemove(item)}
              >
                <Icon as={XMarkIcon} h={4} w={4} />
              </IconButton>
            </Tooltip>
            <SortableList.DragHandle />
          </>
        ) : (
          <Tooltip content='System field (cannot be modified)' aria-label={'Lock'}>
            <Icon as={LockClosedIcon} h={4} w={4} color='gray.500' />
          </Tooltip>
        )}
      </Flex>
    </Stack>
  );
};

const SectionTitleItem: React.FC<{ item: ContentTypeField; reload: () => void }> = ({
  item,
  reload,
}) => {
  async function handleRemove(item: ContentTypeField) {
    try {
      await api.delete(`content-type-fields/${item.id}`);

      toaster.create({
        title: 'Section title was successfully removed',
        type: 'success',
      });

      reload();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to delete section title',
      });
    }
  }

  return (
    <Stack key={item.id} borderBottomWidth={'2px'}>
      <Flex justifyContent={'center'}>
        <Text textStyle='lg' fontWeight='medium'>
          {item.field_name}
        </Text>
        <Spacer />
        <Tooltip content={'Delete'} aria-label={'Delete'}>
          <IconButton
            aria-label={'Delete'}
            variant={'ghost'}
            size={'sm'}
            onClick={() => handleRemove(item)}
          >
            <Icon as={XMarkIcon} h={4} w={4} />
          </IconButton>
        </Tooltip>
        <SortableList.DragHandle />
      </Flex>
    </Stack>
  );
};

export default function ListContentTypeField({ workspaceId, contentTypeId }: Props) {
  const fieldDrawer = useModalToggle();
  const sectionTitleModal = useModalToggle();

  const [staticFields, setStaticFields] = useState<ContentTypeField[]>([]);
  const [sortableFields, setSortableFields] = useState<ContentTypeField[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    if (!contentTypeId || !workspaceId) return;

    setIsLoading(true);

    try {
      const response = await api.get(`content-type-fields/${contentTypeId}/${workspaceId}/data`);
      const sortedFields = response.data.data.sort(
        (a: ContentTypeField, b: ContentTypeField) => a.sort_order - b.sort_order,
      );

      const staticFieldsList: ContentTypeField[] = [];
      const sortableFieldsList: ContentTypeField[] = [];

      sortedFields.forEach((field: ContentTypeField) => {
        if (isStaticField(field.field_unique_name)) {
          staticFieldsList.push(field);
        } else {
          sortableFieldsList.push(field);
        }
      });

      setStaticFields(staticFieldsList);
      setSortableFields(sortableFieldsList);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch field',
      });
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, contentTypeId]);

  const handleSortEnd = useCallback(
    async (newItems: ContentTypeField[]) => {
      setSortableFields(newItems);

      const fieldIds = staticFields.map((item) => item.id).concat(newItems.map((item) => item.id));

      const body = fieldIds.map((id) => ({
        content_type_field_id: id,
      }));

      try {
        await api.put(`content-type-fields/sort-order`, body);
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to update sort order',
          duration: 3000,
        });

        fetchData();
      }
    },
    [fetchData, staticFields],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function reloadData() {
    setTimeout(() => {
      fetchData();
    }, 1000);
  }

  const allFields = useMemo(() => {
    return [...staticFields, ...sortableFields];
  }, [staticFields, sortableFields]);

  return (
    <Box>
      <VStack direction={'row'} gap={4} align={'stretch'}>
        <Flex>
          <Heading size={'xl'} mt={2}>
            Content structure
          </Heading>
          <Spacer />
          <Stack direction={'row'} gap={4}>
            <Button colorPalette='brand' onClick={sectionTitleModal.open}>
              <Icon as={PlusIcon} w={4} h={4} />
              Add section title
            </Button>
            <Button colorPalette='brand' onClick={fieldDrawer.open}>
              <Icon as={PlusIcon} w={4} h={4} />
              Add fields
            </Button>
          </Stack>
        </Flex>
        <Separator my={1} />

        {isLoading && (
          <Center py={10}>
            <Spinner size='xl' />
          </Center>
        )}

        {staticFields.length > 0 && (
          <Box mb={4} px={3}>
            <Stack gap={4}>
              {staticFields.map((item) => (
                <ContentTypeItem key={`static-${item.id}`} item={item} reload={reloadData} />
              ))}
            </Stack>
          </Box>
        )}

        <Box px={3}>
          <>
            <SortableList
              items={sortableFields}
              onChange={handleSortEnd}
              renderItem={(item: ContentTypeField) => (
                <SortableList.Item id={item.id}>
                  {item.field_type === BlockType.SectionTitle ? (
                    <SectionTitleItem item={item} reload={reloadData} />
                  ) : (
                    <ContentTypeItem item={item} reload={reloadData} />
                  )}
                </SortableList.Item>
              )}
            />
          </>
          {sortableFields.length === 0 && !isLoading && (
            <Box
              textAlign='center'
              ml={5}
              p={6}
              borderWidth='1px'
              borderRadius='md'
              borderStyle='dashed'
            >
              <Text color='fg.muted' fontSize='sm'>
                No custom fields added yet. Click "Add fields" to begin.
              </Text>
            </Box>
          )}
        </Box>
      </VStack>

      <CreateContentTypeField
        {...fieldDrawer.props}
        workspaceId={workspaceId}
        contentTypeId={contentTypeId}
        existingFields={allFields}
        onSuccess={() => reloadData()}
      />

      <CreateSectionTitleModal
        {...sectionTitleModal.props}
        workspaceId={workspaceId}
        contentTypeId={contentTypeId}
        onSuccess={() => reloadData()}
      />
    </Box>
  );
}
